// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Settings Page
// Route: /settings
// apps/web/src/routes/settings.tsx
//
// AI Provider configuration, tenant settings, account overview.
// Manage Claude, GPT-4, Grok, Gemini providers with usage tracking.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  PulseDot,
  EngineTooltip,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useAIProviders, useAIProviderMutations, useModelList } from '../lib/api/effort-reports';
import { useApiClient } from '@forgetrack/api-client';
import type { AIProvider, ProviderType, ModelInfo } from '../lib/api/effort-reports';
import { useContextHelp } from '../components/engine/ContextHelpProvider';
import { HelpBubble } from '../components/engine/HelpBubble';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

// ─── Provider Type Config ───────────────────────────────────────────────────

const PROVIDER_CONFIG: Record<ProviderType, { color: string; icon: string; label: string }> = {
  Anthropic: { color: '#d4a27f', icon: '🟤', label: 'Anthropic (Claude)' },
  OpenAI: { color: C.neon, icon: '🟢', label: 'OpenAI (GPT-4)' },
  xAI: { color: C.amber, icon: '🟡', label: 'xAI (Grok)' },
  Google: { color: C.blue, icon: '🔵', label: 'Google (Gemini)' },
  Ollama: { color: C.violet, icon: '🟣', label: 'Ollama (Local)' },
  Custom: { color: C.pink, icon: '🔮', label: 'Custom Provider' },
};

// ─── Main Component ─────────────────────────────────────────────────────────

function SettingsPage() {
  const { data: providers, loading, refetch } = useAIProviders();
  const mutations = useAIProviderMutations();
  const { createApi } = useApiClient();
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [activeTab, setActiveTab] = useState<'providers' | 'account' | 'help'>('providers');

  const metrics = useMemo(() => {
    if (!providers) return null;
    const active = providers.filter(p => p.isActive).length;
    const totalTokens = providers.reduce((s, p) => s + p.totalTokensUsed, 0);
    const totalCost = providers.reduce((s, p) => s + p.totalCostUSD, 0);
    return { total: providers.length, active, totalTokens, totalCost };
  }, [providers]);

  const countTokens = useCounter(
    metrics ? Math.round(metrics.totalTokens / 1000) : 0, 700, 300
  );

  return (
    <EnginePageShell
      title="Settings"
      subtitle="CONFIGURATION"
      icon="⚙"
      accentColor={C.blue}
      statusLabel="CONFIG"
      statusColor={C.blue}
    >
      {/* ── Tab navigation ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {(['providers', 'account', 'help'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: activeTab === tab ? `${C.blue}20` : 'transparent',
              border: `1px solid ${activeTab === tab ? `${C.blue}50` : C.faint}`,
              color: activeTab === tab ? C.blue : C.muted,
              fontFamily: F.mono, fontSize: 11, padding: '8px 20px', borderRadius: 8,
              cursor: 'pointer', fontWeight: 600, letterSpacing: '0.08em',
            }}
          >
            {tab === 'providers' ? '🤖 AI PROVIDERS' : tab === 'account' ? '👤 ACCOUNT' : '❓ HELP'}
          </button>
        ))}
      </div>

      {activeTab === 'providers' ? (
        <>
          {/* ── Provider Metrics ───────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
            <MetricCard label="Providers" value={metrics?.total ?? 0} icon="🤖" color={C.blue}
              subtitle={`${metrics?.active ?? 0} active`}
              tooltip={<div>Configured AI providers for estimation and analysis.</div>} />
            <MetricCard label="Total Tokens" value={`${countTokens}K`} icon="🔤" color={C.violet}
              tooltip={<div>Total tokens consumed across all providers.</div>} />
            <MetricCard
              label="Total AI Cost"
              value={`$${(metrics?.totalCost ?? 0).toFixed(2)}`}
              icon="💰" color={C.neon}
              tooltip={<div>Cumulative cost of all AI API calls.</div>}
            />
          </div>

          {/* ── Provider List ──────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <SectionLabel color={C.violet}>Configured Providers</SectionLabel>
            <button
              onClick={() => setShowAddProvider(true)}
              style={{
                background: `${C.neon}12`, border: `1px solid ${C.neon}30`, color: C.neon,
                fontFamily: F.mono, fontSize: 10, padding: '5px 14px', borderRadius: 6, cursor: 'pointer',
              }}
            >
              + ADD PROVIDER
            </button>
            <button
              onClick={async () => {
                const api = await createApi();
                await api.post('/api/v1/ai-providers/sync-pricing');
                refetch();
              }}
              style={{
                background: `${C.amber}15`, color: C.amber, border: `1px solid ${C.amber}40`,
                fontFamily: F.mono, fontSize: 10, padding: '5px 14px', borderRadius: 6, cursor: 'pointer',
              }}
            >
              ⚡ SYNC PRICING
            </button>
          </div>

          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {[0,1].map(i => <GlassPanel key={i} delay={0.3+i*0.08}><LoadingShimmer height={120} /></GlassPanel>)}
            </div>
          ) : providers && providers.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
              {providers.map((provider, i) => (
                <ProviderCard key={provider.id} provider={provider} index={i}
                  onToggle={async () => {
                    await mutations.update(provider.id, { isActive: !provider.isActive });
                    refetch();
                  }}
                  onDelete={async () => {
                    await mutations.remove(provider.id);
                    refetch();
                  }}
                />
              ))}
            </div>
          ) : (
            <GlassPanel accent={C.violet} delay={0.3}>
              <EmptyState icon="🤖" title="No AI providers configured"
                subtitle="Add AI providers to enable AI-assisted effort estimation. Claude (Anthropic) is the primary provider."
                action={
                  <button onClick={() => setShowAddProvider(true)} style={{
                    background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
                    fontFamily: F.mono, fontSize: 12, padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
                  }}>+ ADD PROVIDER</button>
                }
              />
            </GlassPanel>
          )}

          {/* ── Add Provider Modal ─────────────────────────────────────── */}
          {showAddProvider && (
            <AddProviderModal
              onClose={() => setShowAddProvider(false)}
              onCreate={async (data) => {
                const result = await mutations.create(data);
                if (result) { setShowAddProvider(false); refetch(); }
              }}
              loading={mutations.loading}
            />
          )}
        </>
      ) : activeTab === 'help' ? (
        /* ── Help Tab ───────────────────────────────────────────────── */
        <ContextHelpSettings />
      ) : (
        /* ── Account Tab ────────────────────────────────────────────── */
        <AccountSettings />
      )}
    </EnginePageShell>
  );
}

// ─── ProviderCard ───────────────────────────────────────────────────────────

function ProviderCard({ provider, index, onToggle, onDelete }: { provider: AIProvider; index: number; onToggle: () => void; onDelete: () => void }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const cfg = PROVIDER_CONFIG[provider.providerType] || PROVIDER_CONFIG.Custom;

  return (
    <div style={{
      background: C.surface, border: `1px solid ${provider.isActive ? `${cfg.color}25` : C.faint}`,
      borderRadius: 12, padding: 18, position: 'relative', overflow: 'hidden',
      opacity: provider.isActive ? 1 : 0.6,
      animation: `engine-feed-in 0.4s ease ${0.3 + index * 0.06}s both`,
    }}>
      {/* Accent glow */}
      {provider.isActive && (
        <div style={{
          position: 'absolute', top: -20, right: -20, width: 80, height: 80,
          background: `radial-gradient(circle, ${cfg.color}10 0%, transparent 70%)`, pointerEvents: 'none',
        }} />
      )}

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 22 }}>{cfg.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{provider.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <StatusBadge label={cfg.label} color={cfg.color} />
              <StatusBadge
                label={provider.isActive ? 'ACTIVE' : 'INACTIVE'}
                color={provider.isActive ? C.neon : C.dim}
              />
            </div>
          </div>
          {/* Toggle + Delete */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={onToggle}
              style={{
                background: provider.isActive ? `${C.neon}15` : `${C.coral}15`,
                border: `1px solid ${provider.isActive ? `${C.neon}30` : `${C.coral}30`}`,
                color: provider.isActive ? C.neon : C.coral,
                fontFamily: F.mono, fontSize: 9, padding: '4px 12px', borderRadius: 4, cursor: 'pointer',
              }}
            >
              {provider.isActive ? 'DEACTIVATE' : 'ACTIVATE'}
            </button>
            {confirmDelete ? (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => { onDelete(); setConfirmDelete(false); }} style={{
                  background: `${C.coral}20`, border: `1px solid ${C.coral}40`, color: C.coral,
                  fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                }}>CONFIRM</button>
                <button onClick={() => setConfirmDelete(false)} style={{
                  background: 'transparent', border: `1px solid ${C.faint}`, color: C.dim,
                  fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                }}>CANCEL</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} style={{
                background: 'transparent', border: `1px solid ${C.faint}`, color: C.dim,
                fontFamily: F.mono, fontSize: 9, padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
              }}>DELETE</button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
          <EngineTooltip content={<div>Default model used for API calls.</div>}>
            <div>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>MODEL</div>
              <div style={{ fontSize: 13, fontFamily: F.mono, fontWeight: 600, color: C.textSoft }}>{provider.defaultModel}</div>
            </div>
          </EngineTooltip>
          <EngineTooltip content={<div>Total tokens consumed by this provider.</div>}>
            <div>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>TOKENS</div>
              <div style={{ fontSize: 13, fontFamily: F.mono, fontWeight: 600, color: C.violet }}>
                {provider.totalTokensUsed > 1000 ? `${(provider.totalTokensUsed / 1000).toFixed(1)}K` : provider.totalTokensUsed}
              </div>
            </div>
          </EngineTooltip>
          <EngineTooltip content={<div>Total API cost for this provider.</div>}>
            <div>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>COST</div>
              <div style={{ fontSize: 13, fontFamily: F.mono, fontWeight: 600, color: C.neon }}>
                ${provider.totalCostUSD.toFixed(2)}
              </div>
            </div>
          </EngineTooltip>
        </div>

        {/* API key status + endpoint */}
        <div style={{
          display: 'flex', gap: 12, marginBottom: 10, alignItems: 'center',
        }}>
          <EngineTooltip content={<div>{provider.hasApiKey ? 'API key is configured and encrypted at rest.' : 'No API key configured. Add one to enable this provider.'}</div>}>
            <div style={{
              fontSize: 10, fontFamily: F.mono,
              color: provider.hasApiKey ? C.neon : C.coral,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {provider.hasApiKey ? '🔑' : '⚠️'}
              {provider.hasApiKey ? 'KEY CONFIGURED' : 'NO API KEY'}
            </div>
          </EngineTooltip>
          {provider.apiEndpoint && (
            <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
              {provider.apiEndpoint}
            </div>
          )}
        </div>

        {/* Token pricing */}
        <div style={{
          fontSize: 10, fontFamily: F.mono, color: C.dim, paddingTop: 8,
          borderTop: `1px solid ${C.faint}`, display: 'flex', gap: 16,
        }}>
          <span>Input: ${provider.costPerInputToken.toFixed(6)}/tok</span>
          <span>Output: ${provider.costPerOutputToken.toFixed(6)}/tok</span>
        </div>
      </div>
    </div>
  );
}

// ─── AddProviderModal ───────────────────────────────────────────────────────

function AddProviderModal({ onClose, onCreate, loading }: {
  onClose: () => void;
  onCreate: (data: Partial<AIProvider>) => void;
  loading: boolean;
}) {
  const [name, setName] = useState('');
  const [providerType, setProviderType] = useState<ProviderType>('Anthropic');
  const [model, setModel] = useState('claude-sonnet-4-20250514');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [inputCost, setInputCost] = useState('0.000003');
  const [outputCost, setOutputCost] = useState('0.000015');
  const { data: modelList, loading: modelsLoading } = useModelList(providerType);

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
    fontSize: 13, outline: 'none',
  };

  const ENDPOINT_PRESETS: Record<ProviderType, { name: string; endpoint: string }> = {
    Anthropic: { name: 'Claude', endpoint: 'https://api.anthropic.com/v1/messages' },
    OpenAI: { name: 'GPT', endpoint: 'https://api.openai.com/v1/chat/completions' },
    xAI: { name: 'Grok', endpoint: 'https://api.x.ai/v1/chat/completions' },
    Google: { name: 'Gemini', endpoint: 'https://generativelanguage.googleapis.com/v1beta' },
    Ollama: { name: 'Local LLM', endpoint: 'http://localhost:11434/api/generate' },
    Custom: { name: 'Custom', endpoint: '' },
  };

  const selectProvider = (type: ProviderType) => {
    const preset = ENDPOINT_PRESETS[type];
    setProviderType(type);
    setName(preset.name);
    setModel('');
    setApiEndpoint(preset.endpoint);
    setInputCost('0');
    setOutputCost('0');
  };

  const selectModel = (m: ModelInfo) => {
    setModel(m.modelId);
    // Convert per-MTok to per-token for display (divide by 1,000,000)
    if (m.inputCostPerMTok != null) setInputCost((m.inputCostPerMTok / 1_000_000).toFixed(8));
    if (m.outputCostPerMTok != null) setOutputCost((m.outputCostPerMTok / 1_000_000).toFixed(8));
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'engine-tooltip-in 0.2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.panel, border: `1px solid ${C.violet}20`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 520,
      }}>
        <SectionLabel color={C.violet}>Add AI Provider</SectionLabel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>PROVIDER TYPE</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {(Object.keys(PROVIDER_CONFIG) as ProviderType[]).map(type => {
                const cfg = PROVIDER_CONFIG[type];
                return (
                  <button key={type} onClick={() => selectProvider(type)} style={{
                    background: providerType === type ? `${cfg.color}20` : 'transparent',
                    border: `1px solid ${providerType === type ? `${cfg.color}50` : C.faint}`,
                    color: providerType === type ? cfg.color : C.muted,
                    fontFamily: F.mono, fontSize: 10, padding: '8px', borderRadius: 6, cursor: 'pointer',
                  }}>
                    {cfg.icon} {type}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>NAME</div>
            <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
              DEFAULT MODEL {modelsLoading && <span style={{ color: C.amber }}>loading...</span>}
            </div>
            {modelList && modelList.length > 0 ? (
              <select
                value={model}
                onChange={e => {
                  const found = modelList.find(m => m.modelId === e.target.value);
                  if (found) selectModel(found);
                  else setModel(e.target.value);
                }}
                style={{ ...inputStyle, appearance: 'auto' as const }}
              >
                <option value="">Select model...</option>
                {modelList.map(m => (
                  <option key={m.modelId} value={m.modelId}>
                    {m.displayName}{m.inputCostPerMTok != null ? ` ($${m.inputCostPerMTok}/${m.outputCostPerMTok} per MTok)` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input value={model} onChange={e => setModel(e.target.value)} placeholder="e.g. claude-sonnet-4-20250514" style={inputStyle} />
            )}
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>API ENDPOINT</div>
            <input value={apiEndpoint} onChange={e => setApiEndpoint(e.target.value)} placeholder="https://api.anthropic.com/v1/messages" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>API KEY</div>
            <input value={apiKey} onChange={e => setApiKey(e.target.value)} type="password" placeholder="sk-..." style={inputStyle} />
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, marginTop: 4 }}>Encrypted at rest. Never displayed after saving.</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>INPUT $/TOKEN</div>
              <input value={inputCost} onChange={e => setInputCost(e.target.value)} type="number" step="0.000001" style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>OUTPUT $/TOKEN</div>
              <input value={outputCost} onChange={e => setOutputCost(e.target.value)} type="number" step="0.000001" style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted, fontFamily: F.mono, fontSize: 11, padding: '8px 20px', borderRadius: 6, cursor: 'pointer' }}>CANCEL</button>
          <button
            disabled={!name.trim() || !model.trim() || loading}
            onClick={() => onCreate({
              name: name.trim(),
              providerType,
              defaultModel: model.trim(),
              apiEndpoint: apiEndpoint.trim() || undefined,
              apiKey: apiKey.trim() || undefined,
              isActive: true,
              costPerInputToken: parseFloat(inputCost) || 0,
              costPerOutputToken: parseFloat(outputCost) || 0,
            })}
            style={{
              background: name.trim() ? `${C.neon}20` : `${C.dim}10`,
              border: `1px solid ${name.trim() ? `${C.neon}40` : C.faint}`,
              color: name.trim() ? C.neon : C.dim,
              fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '8px 24px',
              borderRadius: 6, cursor: name.trim() ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'ADDING...' : 'ADD PROVIDER'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── AccountSettings ────────────────────────────────────────────────────────

function AccountSettings() {
  return (
    <GlassPanel accent={C.blue} delay={0.2}>
      <SectionLabel color={C.blue}>Tenant Information</SectionLabel>
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.8 }}>
        Tenant configuration is managed through the Entra ID admin portal and Azure environment variables.
        Subdomain routing, custom domains, branding, and subscription tier are configured at the infrastructure level.
      </div>

      <div style={{ marginTop: 20 }}>
        <SectionLabel color={C.violet}>Configuration Checklist</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: 'Entra ID app registration', status: 'Configured', color: C.neon },
            { label: 'API environment variables', status: 'Set', color: C.neon },
            { label: 'Database schemas', status: 'Deployed', color: C.neon },
            { label: 'MSAL auth flow', status: 'Working', color: C.neon },
            { label: 'CI/CD pipelines', status: 'Green', color: C.neon },
            { label: 'Stripe billing', status: 'Pending', color: C.amber },
            { label: 'Git integration', status: 'Pending', color: C.amber },
            { label: 'Custom domain SSL', status: 'Pending', color: C.amber },
          ].map((item, i) => (
            <div key={item.label} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
              background: 'rgba(0,0,0,0.2)', borderRadius: 6,
              opacity: 0, animation: `engine-feed-in 0.3s ease ${0.3 + i * 0.04}s forwards`,
            }}>
              <PulseDot color={item.color} size={6} />
              <span style={{ flex: 1, fontSize: 12, color: C.textSoft }}>{item.label}</span>
              <StatusBadge label={item.status} color={item.color} />
            </div>
          ))}
        </div>
      </div>
    </GlassPanel>
  );
}

// ─── ContextHelpSettings ────────────────────────────────────────────────────

const HELP_ACCENT = C.cyan;

function ContextHelpSettings() {
  const { enabled, setEnabled, loading, loaded } = useContextHelp();

  const categoryExamples = [
    { key: 'metric', icon: '📊', label: 'Metrics', desc: 'Compression ratio, replacement cost, AI spend, etc.', color: C.neon },
    { key: 'navigation', icon: '🧭', label: 'Navigation', desc: 'Sidebar pages, sections, and route descriptions.', color: C.blue },
    { key: 'action', icon: '⚡', label: 'Actions', desc: 'Buttons and operations: generate report, sync repo, etc.', color: C.amber },
    { key: 'concept', icon: '💡', label: 'Concepts', desc: 'Core concepts: phases, benchmarks, equivalent effort.', color: C.violet },
    { key: 'field', icon: '📝', label: 'Fields', desc: 'Form fields and data points: status, type, complexity.', color: C.pink },
    { key: 'status', icon: '🔵', label: 'Status', desc: 'Status indicators: sync state, health checks, live dots.', color: C.cyan },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Toggle Panel ── */}
      <GlassPanel accent={HELP_ACCENT} delay={0.2}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <SectionLabel color={HELP_ACCENT}>Context Help</SectionLabel>
            <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.7, marginTop: 4 }}>
              When enabled, small help indicators appear next to metrics, fields, and actions
              throughout the application. Hover over them to see detailed explanations of what
              each element means, how it's calculated, and what you can do with it.
            </div>
          </div>

          {/* Toggle switch */}
          <button
            onClick={() => setEnabled(!enabled)}
            style={{
              width: 52,
              height: 28,
              borderRadius: 14,
              border: 'none',
              background: enabled
                ? `linear-gradient(135deg, ${HELP_ACCENT}, ${HELP_ACCENT}90)`
                : `${C.dim}30`,
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.3s ease',
              flexShrink: 0,
              boxShadow: enabled ? `0 0 12px ${C.cyanGlow}` : 'none',
            }}
          >
            <div style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: enabled ? '#fff' : C.muted,
              position: 'absolute',
              top: 3,
              left: enabled ? 27 : 3,
              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontSize: 10,
                fontWeight: 800,
                color: enabled ? HELP_ACCENT : C.dim,
                fontFamily: F.mono,
              }}>
                ?
              </span>
            </div>
          </button>
        </div>

        {/* Status indicator */}
        <div style={{
          marginTop: 16,
          padding: '10px 14px',
          background: enabled ? `${HELP_ACCENT}08` : 'rgba(0,0,0,0.2)',
          borderRadius: 8,
          border: `1px solid ${enabled ? `${HELP_ACCENT}20` : C.faint}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          transition: 'all 0.3s ease',
        }}>
          <PulseDot color={enabled ? HELP_ACCENT : C.dim} size={6} />
          <span style={{
            fontSize: 11,
            fontFamily: F.mono,
            color: enabled ? HELP_ACCENT : C.dim,
            letterSpacing: '0.06em',
            fontWeight: 600,
          }}>
            {loading ? 'LOADING HELP DATA...' : enabled ? 'CONTEXT HELP ACTIVE' : 'CONTEXT HELP DISABLED'}
          </span>
          {loaded && enabled && (
            <span style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontFamily: F.mono,
              color: C.dim,
            }}>
              Data loaded
            </span>
          )}
        </div>
      </GlassPanel>

      {/* ── Preview ── */}
      {enabled && (
        <GlassPanel accent={HELP_ACCENT} delay={0.35}>
          <SectionLabel color={HELP_ACCENT}>Preview</SectionLabel>
          <div style={{
            fontSize: 12,
            color: C.muted,
            marginBottom: 12,
            lineHeight: 1.6,
          }}>
            Hover the help indicator on the example below to see how context help looks:
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: 'rgba(0,0,0,0.25)',
            borderRadius: 8,
            border: `1px solid ${C.faint}`,
          }}>
            <HelpBubble helpKey="settings.context_help" indicatorPosition="inline-end">
              <span style={{
                fontFamily: F.mono,
                fontSize: 14,
                fontWeight: 700,
                color: HELP_ACCENT,
              }}>
                Context Help
              </span>
            </HelpBubble>
            <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono }}>
              ← hover the ? indicator
            </span>
          </div>
        </GlassPanel>
      )}

      {/* ── Category Reference ── */}
      <GlassPanel accent={C.violet} delay={0.45}>
        <SectionLabel color={C.violet}>Help Categories</SectionLabel>
        <div style={{
          fontSize: 12,
          color: C.muted,
          marginBottom: 14,
          lineHeight: 1.6,
        }}>
          Context help entries are organized into categories. Each category has a distinct icon
          that appears in the help tooltip header.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
          {categoryExamples.map((cat, i) => (
            <div key={cat.key} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: 6,
              border: `1px solid ${cat.color}10`,
              opacity: 0,
              animation: `engine-feed-in 0.3s ease ${0.5 + i * 0.06}s forwards`,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{cat.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: cat.color,
                  fontFamily: F.mono,
                  letterSpacing: '0.04em',
                }}>
                  {cat.label}
                </div>
                <div style={{
                  fontSize: 10,
                  color: C.dim,
                  marginTop: 2,
                  lineHeight: 1.4,
                }}>
                  {cat.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </GlassPanel>
    </div>
  );
}
